import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { CheckCircle2 } from "lucide-react";

/**
 * Curriculum map keyed by tier id. Each curriculum has:
 *  - code (mono eyebrow)
 *  - price
 *  - headline
 *  - subheadline
 *  - modules[]: { label, title, bullets[], outcome? }
 */
const CURRICULA = {
  pro: {
    code: "T2 / PRO · MASTER HUNTER CERTIFICATION",
    price: "₹7,499",
    headline: "The 4-Week Strategic Sales Sprint",
    subheadline: 'Move from a "Product Seller" to a "Market Architect."',
    modules: [
      {
        label: "Week 01",
        title: "Territory Intelligence & GTM Strategy",
        bullets: [
          'Mapping high-value territories and "whitespace" opportunities',
          "Identifying high-propensity accounts in complex verticals (Industrial, Pharma, Electronics, Defence, Tech)",
        ],
        outcome: "Your personalized 90-day territory blueprint.",
      },
      {
        label: "Week 02",
        title: 'The "Infiltration" Framework — Approach & Access',
        bullets: [
          'Navigating the "Gatekeeper Maze" in institutional procurement',
          "Building Professional Equity: mastering personal branding to engage as a peer to CXOs and MDs",
        ],
        outcome: "A high-authority outreach script bank that gets replies.",
      },
      {
        label: "Week 03",
        title:
          "Value Engineering, Technical Negotiation & Sophisticated Communication",
        bullets: [
          "Quantifying ROI for complex solutions and technical hardware",
          "Communication methods that win: advanced objection handling and value-based negotiation",
        ],
        outcome:
          'A custom "Value-Proposition" deck for your specific product.',
      },
      {
        label: "Week 04",
        title: "The Closing Engine & Follow-Up Mastery",
        bullets: [
          'The "7-Touch Multiplier": designing sophisticated follow-up cadences that build trust',
          'Establishing your personal "Private Equity" through consistent professional delivery',
        ],
        outcome:
          "The Sebyx Hunter Certification and a ready-to-deploy sales OS.",
      },
    ],
  },
  elite: {
    code: "T3 / ELITE · EXECUTIVE 1-ON-1 MENTORSHIP",
    price: "₹24,999",
    headline: "The Strategic Revenue Architecture Intensive",
    subheadline: "30-Day Executive Transformation & Support.",
    modules: [
      {
        label: "Pillar 01",
        title: "Diagnostic Audit",
        bullets: [
          "A full autopsy of your current sales infrastructure and funnel bottlenecks",
        ],
      },
      {
        label: "Pillar 02",
        title: "Custom GTM Design",
        bullets: [
          'Building your specific "0-to-1" motion for new market entry or product launches',
        ],
      },
      {
        label: "Pillar 03",
        title: "Professional Equity Mastery",
        bullets: [
          "Personalized coaching on high-impact communication, interpersonal skills, and leadership presence",
        ],
      },
      {
        label: "Pillar 04",
        title: "Ongoing Implementation",
        bullets: [
          "Direct 1-on-1 access for real-time deal auditing and tactical support during the 30-day window",
        ],
      },
    ],
  },
};

export default function CurriculumDialog({ tierId, open, onOpenChange }) {
  const data = tierId ? CURRICULA[tierId] : null;
  if (!data) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`curriculum-dialog-${tierId}`}
        className="max-w-3xl max-h-[88vh] overflow-y-auto rounded-none border border-sebyx-border bg-sebyx-surface text-sebyx-text p-0 sm:rounded-none"
      >
        {/* Header */}
        <div className="relative border-b border-sebyx-border p-8 md:p-10 pr-16 md:pr-20">
          <DialogHeader className="text-left space-y-3">
            <div className="flex items-center gap-3">
              <span className="block w-8 h-px bg-sebyx-accent" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-sebyx-accent">
                {data.code}
              </span>
            </div>
            <DialogTitle className="font-display text-2xl md:text-4xl font-semibold tracking-tight text-sebyx-text leading-[1.1]">
              {data.headline}
            </DialogTitle>
            <DialogDescription className="font-body text-sebyx-muted text-base md:text-lg leading-relaxed">
              {data.subheadline}
            </DialogDescription>
            <div className="pt-3">
              <span
                data-testid={`curriculum-${tierId}-price`}
                className="inline-flex items-center gap-2 px-3 py-1.5 border border-sebyx-accent text-sebyx-accent font-mono text-[11px] tracking-[0.2em] uppercase"
              >
                Investment · {data.price}
              </span>
            </div>
          </DialogHeader>
        </div>

        {/* Modules */}
        <div className="p-8 md:p-10 space-y-px bg-sebyx-border">
          {data.modules.map((m, i) => (
            <div
              key={i}
              data-testid={`curriculum-${tierId}-module-${i}`}
              className="bg-sebyx-surface p-6 md:p-8 grid grid-cols-1 md:grid-cols-12 gap-6 group hover:bg-sebyx-elevated transition-colors"
            >
              <div className="md:col-span-3">
                <span className="font-mono text-[11px] tracking-[0.3em] uppercase text-sebyx-accent">
                  {m.label}
                </span>
              </div>
              <div className="md:col-span-9">
                <h4 className="font-display text-lg md:text-xl font-medium text-sebyx-text leading-snug mb-4">
                  {m.title}
                </h4>
                <ul className="space-y-2.5">
                  {m.bullets.map((b, bi) => (
                    <li
                      key={bi}
                      className="flex items-start gap-3 text-sm md:text-base text-sebyx-text/90 leading-relaxed"
                    >
                      <span className="block w-1.5 h-1.5 rotate-45 bg-sebyx-accent mt-2 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                {m.outcome && (
                  <div className="mt-5 flex items-start gap-3 border-l-2 border-sebyx-accent pl-4 py-2 bg-sebyx-bg/40">
                    <CheckCircle2
                      size={14}
                      strokeWidth={2}
                      className="text-sebyx-accent shrink-0 mt-1"
                    />
                    <span className="text-sm text-sebyx-text">
                      <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-accent mr-2">
                        Outcome
                      </span>
                      {m.outcome}
                    </span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="border-t border-sebyx-border p-6 md:p-8 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between bg-sebyx-bg">
          <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-sebyx-muted">
            Seats are intentionally limited · Applications reviewed weekly
          </span>
          <a
            href="#contact"
            data-testid={`curriculum-${tierId}-cta`}
            onClick={() => onOpenChange(false)}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-sebyx-accent text-sebyx-bg font-semibold text-sm hover:bg-[#3FA097] transition-colors"
          >
            Apply for {tierId === "pro" ? "Cohort" : "Mentorship"}
          </a>
        </div>

        {/* Custom close (shadcn already renders one; ensure accent hover) */}
      </DialogContent>
    </Dialog>
  );
}
